import { useEffect, useState, useCallback, useRef } from "react";
import { sharedGet } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import LevelUpOverlay from "@/components/LevelUpOverlay";

const LAST_LEVEL_KEY = "solano_last_level_seen";

/**
 * Global level-up watcher — mounted once at the app root. On mount and on
 * every visibility change (user returns to the tab) it fetches the current
 * gamification summary and compares the level index to the persisted
 * `solano_last_level_seen`. When it detects an increase (and there is a
 * previous baseline) it renders the full-screen `<LevelUpOverlay>` with
 * confetti. First-time users (no baseline stored) never see the overlay;
 * we just record their current level to establish a baseline.
 *
 * Also exposes `window.solanoLevelCheck()` so other flows (e.g. after a
 * purchase that awards SP, or after a workout log) can request a fresh
 * check without waiting for a tab-focus event.
 */
export default function LevelUpWatcher() {
  const { user } = useAuth() || {};
  const [levelUp, setLevelUp] = useState(null); // holds `level.current` when active
  const inFlight = useRef(false);

  const check = useCallback(async () => {
    // Only clients need this. Ignore anonymous or nutritionist role.
    if (!user || user.role !== "client") return;
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await sharedGet("/client/gamification/summary");
      const cur = data?.level?.current;
      if (!cur) return;
      let lastSeen = 0;
      try { lastSeen = parseInt(localStorage.getItem(LAST_LEVEL_KEY) || "0", 10) || 0; } catch {}
      if (lastSeen > 0 && cur.idx > lastSeen) {
        setLevelUp(cur);
      }
      // Always update the baseline so we celebrate each new tier exactly once.
      try { localStorage.setItem(LAST_LEVEL_KEY, String(cur.idx)); } catch {}
    } catch {
      // Silent — background check, don't disturb the UI.
    } finally {
      inFlight.current = false;
    }
  }, [user]);

  // First check on mount / login
  useEffect(() => { check(); }, [check]);

  // Re-check when the tab regains focus (fresh SP after phone activity, etc.)
  useEffect(() => {
    const handler = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [check]);

  // Expose a manual trigger so purchase flows can force an immediate check.
  useEffect(() => {
    window.solanoLevelCheck = check;
    return () => { if (window.solanoLevelCheck === check) delete window.solanoLevelCheck; };
  }, [check]);

  return (
    <LevelUpOverlay open={!!levelUp} level={levelUp} onClose={() => setLevelUp(null)} />
  );
}
